import { ChevronDown, ChevronUp } from '@untitledui/icons';
import type { SortDescriptor } from 'react-aria-components';

import { cx } from '@/utils/cx';

export interface SortableHeaderProps {
  /** Column identifier used in SortDescriptor */
  columnKey: string;
  /** Display label */
  label: string;
  /** Current active sort descriptor */
  sortDescriptor: SortDescriptor | null | undefined;
  /** Called when user clicks to change sort */
  onSortChange: (descriptor: SortDescriptor | null) => void;
  /** Extra className for the header cell wrapper */
  className?: string;
}

/**
 * SortableHeader — clickable column header that cycles sort state.
 *
 * Click cycle: none → ascending → descending → none
 * Announces sort changes via the parent table's aria-live region.
 */
export function SortableHeader({
  columnKey,
  label,
  sortDescriptor,
  onSortChange,
  className,
}: SortableHeaderProps) {
  const isActive = sortDescriptor?.column === columnKey;
  const direction = isActive ? sortDescriptor?.direction : undefined;

  const handleClick = () => {
    if (!isActive) {
      onSortChange({ column: columnKey, direction: 'ascending' });
    } else if (direction === 'ascending') {
      onSortChange({ column: columnKey, direction: 'descending' });
    } else {
      // descending → clear
      onSortChange(null);
    }
  };

  // aria-sort belongs on the parent columnheader, not on this nested button.
  // No manual onKeyDown for Enter/Space — native <button> handles keyboard
  // activation, so adding handlers would cause double sort toggles.
  return (
    <button
      type='button'
      onClick={handleClick}
      className={cx(
        'group flex h-full w-full items-center gap-1 px-3 text-xs font-medium select-none',
        'text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)]',
        'transition-colors duration-75 cursor-pointer outline-none',
        'focus-visible:ring-2 focus-visible:ring-[var(--color-brand-600)] focus-visible:ring-inset',
        isActive && 'text-[var(--color-text-primary)]',
        className
      )}
    >
      <span className='truncate'>{label}</span>
      <span className='ml-auto flex-shrink-0 w-3.5 h-3.5'>
        {isActive && direction === 'ascending' && (
          <ChevronUp
            aria-hidden='true'
            className='size-3.5 text-fg-brand-primary'
          />
        )}
        {isActive && direction === 'descending' && (
          <ChevronDown
            aria-hidden='true'
            className='size-3.5 text-fg-brand-primary'
          />
        )}
        {!isActive && (
          <ChevronUp
            aria-hidden='true'
            className='size-3.5 opacity-0 group-hover:opacity-40'
          />
        )}
      </span>
    </button>
  );
}
